import type { TFunction } from 'i18next';

import type {
  CustomField,
  IBlackbaudContactMapping,
  TBlackbaudContactMappingKey,
} from '@feathr/blackbox';
import { defaultPersonAttributes, EIntegrationSyncRule } from '@feathr/blackbox';
import type { IIntegrationUIFieldOption, IIntegrationUIMapping } from '@feathr/components';
import { isCustomFieldDataType } from '@feathr/components';
import type {
  IAddedMappingChanges,
  ICrmMappingRules,
  IMappingChanges as ICrmMappingChanges,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.utils';
import {
  getAddedMappingChanges as getCrmAddedMappingChanges,
  getMappingChanges as getCrmMappingChanges,
  lockManagedFeathrFields as lockCrmManagedFeathrFields,
  toUIMapping as toCrmUIMapping,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.utils';

export {
  getMappingWarning,
  hasMappingChanges,
  orderMappingUpdates,
  toMappingUpdate,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.utils';
export type {
  IAddedMappingChanges,
  IMappingAddition,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.utils';

export type IMappingChanges = ICrmMappingChanges<TBlackbaudContactMappingKey>;

/** Default person attributes a mapping can target. v1 leaves out the all-opt-outs flag too. */
const DEFAULT_FIELD_OPTIONS: readonly IIntegrationUIFieldOption[] = defaultPersonAttributes
  .filter(({ id }) => id !== 'pp_opt_outs.all')
  .flatMap(({ data_type: dataType, id, u_key: name }) =>
    isCustomFieldDataType(dataType) ? [{ dataType, id, name }] : [],
  );

/** The Feathr fields a mapping can target: the default person attributes, then the custom fields. */
export function getFeathrFieldOptions(
  customFields: readonly CustomField[],
): IIntegrationUIFieldOption[] {
  const customFieldOptions = customFields.flatMap((field) => {
    const dataType = field.get('data_type');
    return isCustomFieldDataType(dataType)
      ? [{ dataType, id: field.id, name: field.get('u_key') }]
      : [];
  });
  return [...DEFAULT_FIELD_OPTIONS, ...customFieldOptions];
}

/** Blackbaud fields the server points at a Feathr field it manages when it creates the mapping. */
const SERVER_BOUND_KEYS: ReadonlySet<string> = new Set<TBlackbaudContactMappingKey>([
  'emails',
  'solicit_codes',
]);

/** Raiser's Edge mapping behavior for the shared mapping logic. */
export const RAISERS_EDGE_MAPPING_RULES: ICrmMappingRules<IBlackbaudContactMapping> = {
  serverBoundKeys: SERVER_BOUND_KEYS,
  getRemoveDisabledReason: (mapping, t) => {
    if (mapping.has_provisioning_subscriptions) {
      return t('This mapping is being set up in Blackbaud. Try again in a few minutes.');
    }
    return mapping.sync_rule === EIntegrationSyncRule.bothWays
      ? t('Set this mapping to Blackbaud to Feathr before removing it.')
      : undefined;
  },
  locksFeathrField: (mapping) => mapping.sync_rule === EIntegrationSyncRule.bothWays,
};

/**
 * A saved mapping as a table row. The row key is the Blackbaud field key, so each row keeps the
 * v1 `sync-mapping-<key>` e2e name.
 */
export function toUIMapping(
  mapping: IBlackbaudContactMapping,
  t: TFunction,
): IIntegrationUIMapping {
  return toCrmUIMapping(mapping, t, RAISERS_EDGE_MAPPING_RULES);
}

export function lockManagedFeathrFields(
  rows: readonly IIntegrationUIMapping[],
  saved: readonly IBlackbaudContactMapping[],
): IIntegrationUIMapping[] {
  return lockCrmManagedFeathrFields(rows, saved, RAISERS_EDGE_MAPPING_RULES);
}

export function getMappingChanges(
  saved: readonly IBlackbaudContactMapping[],
  rows: readonly IIntegrationUIMapping[],
): IMappingChanges {
  return getCrmMappingChanges(saved, rows, SERVER_BOUND_KEYS);
}

export function getAddedMappingChanges(
  created: readonly IBlackbaudContactMapping[],
  changes: Pick<IMappingChanges, 'add' | 'assign'>,
): IAddedMappingChanges {
  return getCrmAddedMappingChanges(created, changes, SERVER_BOUND_KEYS);
}
